import { useCallback, useEffect, useRef, useState } from 'react';
import Field from '../../components/common/Field';
import TimePicker from '../../components/common/TimePicker';
import ImageGalleryPicker from '../../components/common/ImageGalleryPicker';
import Spinner from '../../components/common/Spinner';
import imagesService from '../../api/services/images.service';
import eventsService from '../../api/services/events.service';
import noSchoolService from '../../api/services/noSchool.service';
import useContextStore, { selectCurrentContext, scopeOfContext } from '../../store/useContextStore';
import useEventsStore from '../../store/useEventsStore';
import useMyProfileStore from '../../store/useMyProfileStore';
import useRoomParentsStore from '../../store/useRoomParentsStore';
import { showToast } from '../../store/useToastStore';
import { fmtDate, fmtEventTime, fmtNoSchoolDates, noSchoolDayCount } from '../../utils/formatters';
import { ALL_DAY_START, ALL_DAY_END, isAllDay } from '../../utils/time';
import { getTokenSubject, getStoredUser } from '../../utils/token';
import useClickOutside from '../../hooks/useClickOutside';

const CalIcon = () => (
  <svg className="cal-ic" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
);

/* Master Calendar only — an event, or a school-wide day off. Tony, 30 Sep: "We
   just need to have a no school option and then set the date... I should just
   be able to type in the title and then set a date." No School strips the form
   to a title and a date: no time, no RSVP, no email. */
const ADD_KINDS = [
  {
    noSchool: false,
    title: 'Event',
    sub: 'Something happening. Movie night, book fair, picture day.',
    icon: <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>,
  },
  {
    noSchool: true,
    title: 'No School',
    sub: 'A day off for every class. Just a title and a date.',
    icon: <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4M9.5 13.5l5 5M14.5 13.5l-5 5" /></svg>,
  },
];

const TIME_MODES = [
  {
    allDay: false,
    title: 'Set a time',
    sub: 'Pick a start and end time.',
    icon: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  },
  {
    allDay: true,
    title: 'All day',
    sub: 'Just the date. Picture Day, Pizza Day, Halloween.',
    icon: <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>,
  },
];

/* New/edit event. Saving never sends — Send to Parents / Schedule Email live on
   the calendar rows. Pass `editingEvent` (with a fresh key) to edit. */
export default function EventForm({ open, editingEvent, onCancel, onSaved }) {
  const ctx = useContextStore(selectCurrentContext);
  const me = useMyProfileStore((s) => s.me);
  const { addEvent, updateEvent } = useEventsStore();
  const logActivity = useRoomParentsStore((s) => s.logActivity);

  const isMaster = ctx.type === 'master';
  /* A day off is a different record from an event (a NoSchool note, saved
     through its own API), so the kind is picked when adding and fixed once
     saved — editing opens the record's own form. */
  const [noSchool, setNoSchool] = useState(editingEvent?.mode === 'noSchool');
  const [title, setTitle] = useState(editingEvent?.title || '');
  const [date, setDate] = useState(editingEvent?.date || '');
  /* No School only: the last day of a break; '' for a single day off */
  const [endDate, setEndDate] = useState(editingEvent?.endDate || '');
  const editingAllDay = !!editingEvent && isAllDay(editingEvent.startTime, editingEvent.endTime);
  const [allDay, setAllDay] = useState(editingAllDay);
  /* an all-day event's 23:59 isn't a picker option — its pickers open on the defaults */
  const [startTime, setStartTime] = useState((!editingAllDay && editingEvent?.startTime) || '09:00');
  const [endTime, setEndTime] = useState((!editingAllDay && editingEvent?.endTime) || '10:00');
  /* what actually gets saved — All day pins the event to 09:00–23:59 */
  const evStart = allDay ? ALL_DAY_START : startTime;
  const evEnd = allDay ? ALL_DAY_END : endTime;
  const [desc, setDesc] = useState(
    editingEvent?.desc && editingEvent.desc !== 'No description added yet.' ? editingEvent.desc : ''
  );
  const [image, setImage] = useState(editingEvent?.image || null);
  /* set only when the cover was uploaded from disk — Unsplash picks stay URLs */
  const [imageFile, setImageFile] = useState(null);
  const [mode, setMode] = useState(
    ['invite', 'noRsvp', 'studentsOnly'].includes(editingEvent?.mode) ? editingEvent.mode : 'invite'
  );
  /* digits only; blank = no limit */
  const [spots, setSpots] = useState(editingEvent?.spots ? String(editingEvent.spots) : '');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ── location picker (Google Places via /public/event/search-address) ── */
  const [locQuery, setLocQuery] = useState(editingEvent?.locationName || '');
  const [locSel, setLocSel] = useState(null);
  const [locResults, setLocResults] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const locRef = useRef(null);

  useClickOutside(locRef, useCallback(() => setLocResults([]), []));

  useEffect(() => {
    const q = locQuery.trim();
    if (locSel || q.length < 3) {
      setLocResults([]);
      setLocLoading(false);
      return undefined;
    }
    const t = setTimeout(async () => {
      setLocLoading(true);
      try {
        setLocResults(await eventsService.searchAddress(q));
      } catch {
        setLocResults([]);
      } finally {
        setLocLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [locQuery, locSel]);

  const pickLocation = (r) => {
    setLocSel(r);
    setLocQuery(r.name && r.address ? `${r.name} — ${r.address}` : r.name || r.address);
    setLocResults([]);
  };

  const handleImageChange = (src, file) => {
    setImage(src);
    setImageFile(file || null);
  };

  /* "Generate image" → Unsplash suggestions for the typed title (8 images). */
  const generateImages = async () => {
    const q = title.trim();
    if (!q) {
      showToast('Enter an event title first, then generate images.');
      return;
    }
    setSuggestionsLoading(true);
    try {
      setSuggestions(await imagesService.searchUnsplash(q, 8));
    } catch (err) {
      showToast(err.message);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  /* A day off: title + first day, and a last day only for a break. A last day
     on the first day is a single day; before it is refused. */
  const saveNoSchool = async () => {
    if (!title.trim()) { window.alert('Give the day a title, like Teacher Planning Day.'); return; }
    if (!date) { window.alert('Pick the day school is closed.'); return; }
    if (endDate && endDate < date) { window.alert('The last day has to be on or after the first day.'); return; }
    const day = { networkId: ctx.id, title: title.trim(), date, endDate: endDate > date ? endDate : '' };
    setSaving(true);
    try {
      if (editingEvent) await noSchoolService.update({ id: editingEvent.id, ...day });
      else await noSchoolService.create(day);
    } catch (err) {
      showToast(err.message);
      setSaving(false);
      return;
    }
    setSaving(false);
    logActivity('Updated Master Calendar', `${editingEvent ? 'Edited' : 'Added'} no school day "${day.title}"`);
    showToast(editingEvent ? 'No school day updated ✓' : 'No school day added to every class calendar ✓');
    onSaved();
  };

  const save = async () => {
    if (noSchool) return saveNoSchool();
    if (!title.trim()) { window.alert('Give the event a title.'); return; }
    if (!date) { window.alert('Pick a date.'); return; }
    if (spots && Number(spots) < 1) { window.alert('Spots must be at least 1 — or leave it blank for no limit.'); return; }
    const scope = scopeOfContext(ctx);
    const draft = {
      title: title.trim(),
      date,
      startTime: evStart,
      endTime: evEnd,
      desc: desc.trim() || 'No description added yet.',
      image,
      mode,
      spots: spots ? Number(spots) : '',
      locationName: locSel ? locSel.name || locSel.address : locQuery.trim(),
    };
    if (editingEvent) {
      /* API events carry string Mongo ids; locally-added rows have numeric ids. */
      if (typeof editingEvent.id === 'string' && editingEvent.id) {
        const patch = { eventId: editingEvent.id };
        if (draft.title !== editingEvent.title) patch.eventName = draft.title;
        if (mode !== (editingEvent.mode || 'invite')) patch.eventType = mode;
        /* the backend validates start < end on the merged result — send both
           whenever the date or either time changed */
        if (date !== editingEvent.date || evStart !== editingEvent.startTime || evEnd !== editingEvent.endTime) {
          patch.eventStart = new Date(`${date}T${evStart || '00:00'}:00`).toISOString();
          patch.eventEnd = new Date(`${date}T${evEnd || evStart || '00:00'}:00`).toISOString();
        }
        if (desc.trim() && desc.trim() !== editingEvent.desc) patch.description = desc.trim();
        /* the backend ignores a blank spots on edit, so a limit can change but not be cleared */
        if (draft.spots && draft.spots !== Number(editingEvent.spots || 0)) patch.spots = draft.spots;
        if (locSel) {
          patch.locationName = locSel.name || locSel.address;
          patch.location = { type: 'Point', coordinates: [locSel.lng, locSel.lat] };
        } else if (locQuery.trim() !== (editingEvent.locationName || '')) {
          patch.locationName = locQuery.trim();
        }
        const coverUrlChanged = !imageFile && image && image !== editingEvent.image;
        if (coverUrlChanged) patch.coverImg = image;
        if (Object.keys(patch).length > 1 || imageFile) {
          setSaving(true);
          try {
            await eventsService.updateEvent(patch, imageFile || undefined);
          } catch (err) {
            showToast(err.message);
            setSaving(false);
            return;
          }
          setSaving(false);
        }
      }
      updateEvent(editingEvent.id, draft);
      if (scope === 'master') logActivity('Updated Master Calendar', `Edited "${draft.title}"`);
      showToast('Event updated ✓');
      onSaved();
      return;
    }

    /* hosts must be the logged-in user — profile fields first, then the login
       response, then the JWT's sub claim (always the auth0 id). */
    const authUser = getStoredUser();
    const myUserId =
      me?.user_id || me?.userId || me?.sub || me?._id ||
      authUser?.user_id || authUser?.userId || authUser?.sub ||
      getTokenSubject() || '';
    if (!myUserId) {
      showToast('Could not resolve your user id — please log in again.');
      return;
    }
    /* eventStart/eventEnd: local date + picked times → UTC ISO ("…T17:00:00.000Z") */
    setSaving(true);
    try {
      await eventsService.createEvent({
        eventName: draft.title,
        eventType: mode,
        eventStart: new Date(`${date}T${evStart || '00:00'}:00`).toISOString(),
        eventEnd: new Date(`${date}T${evEnd || evStart || '00:00'}:00`).toISOString(),
        hosts: [myUserId],
        locationName: draft.locationName,
        location: locSel ? { type: 'Point', coordinates: [locSel.lng, locSel.lat] } : undefined,
        description: desc.trim(),
        spots: draft.spots,
        networkId: ctx.id,
        coverImg: imageFile ? undefined : image || undefined,
        coverFile: imageFile || undefined,
      });
    } catch (err) {
      showToast(err.message);
      setSaving(false);
      return;
    }
    setSaving(false);
    /* keep the calendar view in sync until event listing is API-backed */
    addEvent({ ...draft, scope });
    if (scope === 'master') logActivity('Updated Master Calendar', `Added "${draft.title}"`);
    showToast(
      mode === 'invite' || mode === 'noRsvp'
        ? 'Saved to your calendar — hit Send to Parents on the row when you want them notified ✓'
        : 'Added to calendar ✓'
    );
    onSaved();
  };

  const heading = editingEvent
    ? noSchool ? 'Edit No School Day' : 'Edit Event'
    : noSchool ? 'New No School Day' : 'New Event';
  const saveLabel = saving ? 'Saving…' : editingEvent ? 'Save Changes' : noSchool ? 'Save No School Day' : 'Save Event';
  const dayCount = date ? noSchoolDayCount(date, endDate) : 1;

  return (
    <div className={`inline-form ${open ? 'on' : ''}`}>
      <div className="email-grid">
        <div>
          <h3 style={{ margin: '0 0 14px', fontFamily: 'var(--serif)', fontSize: 17, fontWeight: 600, color: 'var(--ink)' }}>
            {heading}
          </h3>

          {/* the Event / No School choice only exists on the Master Calendar */}
          {isMaster && !editingEvent && (
            <Field label="What are you adding?" style={{ marginBottom: 10 }}>
              <div className="su-kind-grid ev-when-grid" role="radiogroup" aria-label="What are you adding?">
                {ADD_KINDS.map((o) => (
                  <button
                    key={o.title}
                    type="button"
                    role="radio"
                    aria-checked={noSchool === o.noSchool}
                    className={`su-kind-card ${noSchool === o.noSchool ? 'sel' : ''}`}
                    onClick={() => setNoSchool(o.noSchool)}
                  >
                    {o.icon}
                    <span className="t">{o.title}</span>
                    <span className="d">{o.sub}</span>
                  </button>
                ))}
              </div>
            </Field>
          )}

          <Field label="Title" style={{ marginBottom: 10 }}>
            <input
              type="text"
              value={title}
              placeholder={noSchool ? 'e.g. Teacher Planning Day' : 'e.g. Fall Book Fair'}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field label={noSchool ? 'First day' : 'Date'} style={{ marginBottom: 10 }}>
            <div className="date-input-wrap">
              <CalIcon />
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </Field>

          {noSchool && (
            <Field label={<>Last day <span className="opt-flag">optional</span></>} style={{ marginBottom: 10 }}>
              <div className="date-input-wrap">
                <CalIcon />
                <input type="date" value={endDate} min={date || undefined} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <div className="hint" style={{ marginTop: 6 }}>Only for a break longer than one day, like Thanksgiving week.</div>
            </Field>
          )}

          {!noSchool && (
            <>
              <Field label="Time" style={{ marginBottom: 10 }}>
                <div className="su-kind-grid ev-when-grid" role="radiogroup" aria-label="Time">
                  {TIME_MODES.map((o) => (
                    <button
                      key={o.title}
                      type="button"
                      role="radio"
                      aria-checked={allDay === o.allDay}
                      className={`su-kind-card ${allDay === o.allDay ? 'sel' : ''}`}
                      onClick={() => setAllDay(o.allDay)}
                    >
                      {o.icon}
                      <span className="t">{o.title}</span>
                      <span className="d">{o.sub}</span>
                    </button>
                  ))}
                </div>
              </Field>

              {!allDay && (
                <div className="form-grid" style={{ marginBottom: 10 }}>
                  <Field label="Start Time">
                    <TimePicker value={startTime} onChange={setStartTime} />
                  </Field>
                  <Field label="End Time">
                    <TimePicker value={endTime} onChange={setEndTime} />
                  </Field>
                </div>
              )}

              <Field label="Location" style={{ marginBottom: 10 }}>
                <div className="rich-select" ref={locRef} style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={locQuery}
                    placeholder="Search an address or place"
                    autoComplete="off"
                    onChange={(e) => { setLocQuery(e.target.value); setLocSel(null); }}
                  />
                  {locLoading && (
                    <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                      <Spinner variant="green" />
                    </span>
                  )}
                  <div className={`rich-select-menu ${locResults.length ? 'open' : ''}`}>
                    {locResults.map((r, i) => (
                      <div className="rich-select-item" key={i} onClick={() => pickLocation(r)}>
                        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ fontWeight: 600 }}>{r.name || r.address}</span>
                          {r.name && (
                            <span style={{ fontSize: 11, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.address}
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </Field>

              <Field label="Description" style={{ marginBottom: 10 }}>
                <textarea
                  rows={4}
                  value={desc}
                  placeholder="What parents need to know — feel free to use a few lines."
                  onChange={(e) => setDesc(e.target.value)}
                />
              </Field>

              <Field label="Event Image (optional)" style={{ marginBottom: 10 }}>
                <ImageGalleryPicker
                  image={image}
                  onChange={handleImageChange}
                  suggestions={suggestions}
                  suggestionsLoading={suggestionsLoading}
                  onGenerate={generateImages}
                />
              </Field>

              <Field label="What Kind of Event Is This?" style={{ marginBottom: 10 }}>
                <div className="radio-row">
                  <label>
                    <input type="radio" name="evMode" value="invite" checked={mode === 'invite'} onChange={() => setMode('invite')} />
                    📨 Parent Invitation
                    <span style={{ color: 'var(--muted)', fontWeight: 400 }}> — parents need to RSVP</span>
                  </label>
                  <label>
                    <input type="radio" name="evMode" value="noRsvp" checked={mode === 'noRsvp'} onChange={() => setMode('noRsvp')} />
                    No RSVP
                    <span style={{ color: 'var(--muted)', fontWeight: 400 }}> — parents can come, RSVP not required</span>
                  </label>
                  <label>
                    <input type="radio" name="evMode" value="studentsOnly" checked={mode === 'studentsOnly'} onChange={() => setMode('studentsOnly')} />
                    Students-Only
                    <span style={{ color: 'var(--muted)', fontWeight: 400 }}> — no parents at this event</span>
                  </label>
                </div>
              </Field>

              <Field label="Spots (optional)" style={{ marginBottom: 6, maxWidth: 180 }}>
                <input
                  type="text"
                  inputMode="numeric"
                  value={spots}
                  placeholder="No limit"
                  onChange={(e) => setSpots(e.target.value.replace(/\D/g, ''))}
                />
              </Field>
            </>
          )}
        </div>
        <div>
          <Field label="Preview" />
          {noSchool ? (
            /* no email and no public page for a day off — the preview is the 🚫
               row the Sprout Daily Brief uses, as every class calendar shows it */
            <div className="ns-preview">
              <div className="ns-cap">How it shows in Sprout, on every class calendar</div>
              <div className="ns-row">
                <span className="ns-ic" aria-hidden="true">🚫</span>
                <div className="ns-body">
                  <div className="t">{title.trim() || 'No School'}</div>
                  <div className="d">{date ? fmtNoSchoolDates(date, endDate) : 'Pick a date'}</div>
                </div>
                <span className="ns-tag">{dayCount > 1 ? `${dayCount} days` : 'All day'}</span>
              </div>
              <div className="ns-note">
                No invitation, no RSVP, no email. Parents see it on their class calendar and in their Daily Brief that day.
              </div>
            </div>
          ) : (
            <div className="email-preview-card">
              {image && <img className="banner" src={image} alt="" />}
              <div className={`ep-overlap ${image ? '' : 'no-image'}`}>
                <div className="evtitle">{title.trim() || 'Event Title'}</div>
                <div className="ep-chips">
                  <span className="ep-chip">
                    {date ? `${fmtDate(date)} · ${fmtEventTime(evStart, evEnd)}` : 'Pick a date'}
                  </span>
                </div>
              </div>
              <div className="inner">
                <div className="evdesc">{desc.trim() || 'Add a description to see it here.'}</div>
                {/* only Parent Invitation asks for an RSVP — party size, then RSVP */}
                {mode === 'invite' && (
                  <div className="rsvp-row">
                    <span className="rsvp-btn no rsvp-count">👪 Parents <b>1</b></span>
                    <span className="rsvp-btn no rsvp-count">🧒 Kids <b>0</b></span>
                    <span className="rsvp-btn yes rsvp-go">RSVP</span>
                  </div>
                )}
                <div className="foot">Sent from {ctx.name} on RoomParent.com.</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="inline-form-actions">
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" disabled={saving} onClick={save}>
          {saveLabel}
        </button>
      </div>
    </div>
  );
}
